import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const IndustriesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Industry-Specific AI Phone Solutions
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Tailored call flows, compliance guardrails, and proven results for high-call-volume service businesses.
            </p>
          </div>
        </section>

        {/* Law Firms */}
        <section id="law" className="py-20 bg-white scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-teal-light rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-scales-3-line text-4xl text-teal"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">Law Firms</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Never miss a potential client again. Our AI handles initial case intake, qualifies leads, and books consultations—all while following strict legal compliance guidelines.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">New case inquiries and intake</strong>
                      <p className="text-gray-600 text-sm">Capture essential details: case type, incident date, injuries, opposing party</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Consultation scheduling</strong>
                      <p className="text-gray-600 text-sm">Book directly into attorney calendars with conflict checking</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Emergency legal matters routing</strong>
                      <p className="text-gray-600 text-sm">Immediate transfer for time-sensitive cases</p>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-2xl text-amber-600 flex-shrink-0"></i>
                    <div>
                      <strong className="text-navy block mb-2">Compliance Guardrail:</strong>
                      <p className="text-gray-700 text-sm">
                        Strictly intake and scheduling—never provides legal advice or case assessments. All interactions logged for compliance review.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-navy mb-6">Results for Law Firms</h3>
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">73%</div>
                    <p className="text-gray-600 font-medium mb-2">After-hours leads captured</p>
                    <p className="text-sm text-gray-500">Personal injury firm, 30-day pilot</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">$180K</div>
                    <p className="text-gray-600 font-medium mb-2">Additional monthly revenue</p>
                    <p className="text-sm text-gray-500">From previously missed calls</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">92%</div>
                    <p className="text-gray-600 font-medium mb-2">Caller satisfaction rate</p>
                    <p className="text-sm text-gray-500">Based on post-call surveys</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Real Estate */}
        <section id="real-estate" className="py-20 bg-gray-50 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 bg-white rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-navy mb-6">Real Estate Impact</h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">45 mins</div>
                    <p className="text-gray-600 font-medium mb-2">Average time saved per agent daily</p>
                    <p className="text-sm text-gray-500">From phone screening and scheduling</p>
                  </div>
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">3.2x</div>
                    <p className="text-gray-600 font-medium mb-2">More showings booked</p>
                    <p className="text-sm text-gray-500">Compared to voicemail-only approach</p>
                  </div>
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">88%</div>
                    <p className="text-gray-600 font-medium mb-2">Lead qualification accuracy</p>
                    <p className="text-sm text-gray-500">Pre-approved buyers identified correctly</p>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-16 h-16 bg-teal-light rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-home-4-line text-4xl text-teal"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">Real Estate</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Convert more property inquiries into showings. Our AI qualifies buyers and sellers, schedules viewings, and ensures your agents only spend time with serious prospects.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Property showing requests</strong>
                      <p className="text-gray-600 text-sm">Schedule viewings based on agent availability and property access</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Buyer/seller qualification</strong>
                      <p className="text-gray-600 text-sm">Pre-approval status, timeline, budget, and property requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Open house follow-ups</strong>
                      <p className="text-gray-600 text-sm">Automated outreach to attendees with next steps</p>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-2xl text-amber-600 flex-shrink-0"></i>
                    <div>
                      <strong className="text-navy block mb-2">Compliance Guardrail:</strong>
                      <p className="text-gray-700 text-sm">
                        Qualifies leads and books showings; routes hot prospects immediately. No property valuations or contract discussions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Roofing */}
        <section id="roofing" className="py-20 bg-white scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-teal-light rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-building-line text-4xl text-teal"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">Roofing Companies</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Capture storm damage leads before your competitors. Our AI handles estimate requests 24/7, especially critical during peak storm seasons when call volume spikes.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Storm damage estimate requests</strong>
                      <p className="text-gray-600 text-sm">Capture property details, damage type, and urgency level</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Repair and maintenance inquiries</strong>
                      <p className="text-gray-600 text-sm">Schedule inspections and provide service timelines</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Insurance claim assistance</strong>
                      <p className="text-gray-600 text-sm">Collect insurance information and coordinate with adjusters</p>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-2xl text-amber-600 flex-shrink-0"></i>
                    <div>
                      <strong className="text-navy block mb-2">Compliance Guardrail:</strong>
                      <p className="text-gray-700 text-sm">
                        Captures property details and schedules inspections; no binding quotes or warranty promises over the phone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-navy mb-6">Roofing Results</h3>
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">68%</div>
                    <p className="text-gray-600 font-medium mb-2">Reduction in missed calls</p>
                    <p className="text-sm text-gray-500">Multi-location company, 45-day pilot</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">156</div>
                    <p className="text-gray-600 font-medium mb-2">Additional estimates booked</p>
                    <p className="text-sm text-gray-500">During first storm season with AI</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-teal mb-2">24/7</div>
                    <p className="text-gray-600 font-medium mb-2">Coverage during storms</p>
                    <p className="text-sm text-gray-500">When call volume spikes 400%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recruiting */}
        <section id="recruiting" className="py-20 bg-gray-50 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 bg-white rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-navy mb-6">Recruiting Metrics</h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">2.5 hrs</div>
                    <p className="text-gray-600 font-medium mb-2">Saved per recruiter daily</p>
                    <p className="text-sm text-gray-500">From initial phone screening automation</p>
                  </div>
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">85%</div>
                    <p className="text-gray-600 font-medium mb-2">Candidate response rate</p>
                    <p className="text-sm text-gray-500">vs. 40% with voicemail follow-up</p>
                  </div>
                  <div className="border-l-4 border-teal pl-6">
                    <div className="text-3xl font-bold text-navy mb-2">60%</div>
                    <p className="text-gray-600 font-medium mb-2">Faster time-to-interview</p>
                    <p className="text-sm text-gray-500">Immediate scheduling vs. phone tag</p>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-16 h-16 bg-teal-light rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-team-line text-4xl text-teal"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">Recruiting & HR</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Speed up your hiring pipeline. Our AI conducts initial candidate screening, schedules interviews, and ensures top talent doesn't slip through the cracks.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Candidate screening calls</strong>
                      <p className="text-gray-600 text-sm">Verify experience, availability, salary expectations, and job requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Interview scheduling</strong>
                      <p className="text-gray-600 text-sm">Coordinate with hiring managers and send calendar invites</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Job inquiry handling</strong>
                      <p className="text-gray-600 text-sm">Answer basic questions about positions and company culture</p>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-2xl text-amber-600 flex-shrink-0"></i>
                    <div>
                      <strong className="text-navy block mb-2">Compliance Guardrail:</strong>
                      <p className="text-gray-700 text-sm">
                        Initial screening and scheduling only; no hiring decisions or salary negotiations. EEOC-compliant questioning.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to transform your call handling?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              See how PointCalls works for your specific industry.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="/contact" 
                className="px-10 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Schedule a Demo
              </a>
              <a 
                href="tel:+16787376473" 
                className="px-10 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IndustriesPage;